import { authFetch } from '../../../utils/authFetch';
import { useState, useEffect } from 'react';
import { Card as PxCard } from '@roi-pros/px-react';
import toast from 'react-hot-toast';

interface PlanInfo {
  code: string;
  name: string;
  baseCredits: number;
  includedSeats: number;
  perSeatCents: number;
  maxSeats: number | null;
}

interface OutcomeInfo {
  code: string;
  displayName: string;
  includedCredits: number;
}

interface SeatsInfo {
  current: number;
  included: number;
  additional: number;
  perSeatCents: number;
  subtotalCents: number;
}

interface CreditsInfo {
  fromPlan: number;
  fromOutcomes: number;
  monthlyIncluded: number;
  purchasedRemaining: number;
  available: number;
}

interface PricingData {
  plan: PlanInfo;
  outcomes: OutcomeInfo[];
  seats: SeatsInfo;
  credits: CreditsInfo;
  subtotalCents: number;
  totalCents: number;
}

interface SubscriptionData {
  planCode: string;
  planName: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isActive: boolean;
}

interface BillingTabProps {
  workspaceId: string;
  authToken: string;
}

const statusLabels: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past Due',
  canceled: 'Canceled',
};

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  trialing: 'bg-amber-100 text-amber-700',
  past_due: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-500',
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Billing settings tab
 *
 * Features:
 * - Current plan info
 * - Billing status
 * - Trial status
 * - Monthly total breakdown
 * - Outcome pricing
 * - Seat pricing
 * - Stripe portal link
 */
export function BillingTab({ workspaceId, authToken }: BillingTabProps) {
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  async function loadData() {
    try {
      const [pricingRes, subscriptionRes, providerRes] = await Promise.all([
        authFetch(`/api/workspaces/${workspaceId}/pricing`, authToken),
        authFetch('/api/subscription/me', authToken),
        authFetch('/api/billing/provider', authToken),
      ]);

      if (pricingRes.ok) {
        const data = await pricingRes.json();
        setPricing(data.pricing || null);
      }

      if (subscriptionRes.ok) {
        const data = await subscriptionRes.json();
        setSubscription({
          planCode: data.planCode,
          planName: data.planName,
          status: data.status,
          currentPeriodEnd: data.currentPeriodEnd,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd,
          isActive: data.isActive,
        });
      }

      if (providerRes.ok) {
        const data = await providerRes.json();
        setHasStripeCustomer(data.customerIdPresent || false);
      }
    } catch {
      toast.error('Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleOpenPortal() {
    setIsOpeningPortal(true);
    try {
      const response = await authFetch('/api/subscriptions/portal', authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ return_to: window.location.href }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to open billing portal');
      }
    } catch {
      toast.error('Failed to open billing portal');
    } finally {
      setIsOpeningPortal(false);
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan & Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Current Plan</h2>
            <p className="text-2xl font-semibold text-gray-900 mt-2">
              {pricing?.plan.name || subscription?.planName || 'Free'}
            </p>
            {subscription && (
              <div className="mt-3 flex items-center gap-3">
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    statusColors[subscription.status] || statusColors.active
                  }`}
                >
                  {statusLabels[subscription.status] || subscription.status}
                </span>
                {subscription.status === 'trialing' && subscription.currentPeriodEnd && (
                  <span className="text-sm text-gray-500">
                    Trial ends {formatDate(subscription.currentPeriodEnd)}
                  </span>
                )}
                {subscription.cancelAtPeriodEnd && (
                  <span className="text-sm text-red-600">
                    Cancels {formatDate(subscription.currentPeriodEnd)}
                  </span>
                )}
              </div>
            )}
          </div>
          {hasStripeCustomer && (
            <button
              onClick={handleOpenPortal}
              disabled={isOpeningPortal}
              className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50"
            >
              {isOpeningPortal ? 'Opening...' : 'Manage Billing'}
            </button>
          )}
        </div>
        {subscription?.currentPeriodEnd && subscription.status !== 'trialing' && (
          <p className="text-sm text-gray-500 mt-4">
            Next billing date: {formatDate(subscription.currentPeriodEnd)}
          </p>
        )}
      </div>

      {/* Monthly Total */}
      {pricing && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Monthly Summary</h2>
          <div className="space-y-4">
            {/* Base Plan */}
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">{pricing.plan.name} Plan</p>
                <p className="text-sm text-gray-500">
                  {pricing.plan.baseCredits} credits included
                </p>
              </div>
              <p className="text-sm font-medium text-gray-900">
                Included
              </p>
            </div>

            {/* Outcomes */}
            {pricing.outcomes.length > 0 && (
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                  Active Outcomes
                </p>
                {pricing.outcomes.map((outcome) => (
                  <div key={outcome.code} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{outcome.displayName}</p>
                      {outcome.includedCredits > 0 && (
                        <p className="text-sm text-gray-500">
                          +{outcome.includedCredits} credits/mo
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Seats */}
            {pricing.seats.additional > 0 && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Additional Seats</p>
                    <p className="text-sm text-gray-500">
                      {pricing.seats.additional} seats × {formatCents(pricing.seats.perSeatCents)}/seat
                    </p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatCents(pricing.seats.subtotalCents)}
                  </p>
                </div>
              </div>
            )}

            {/* Total */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between">
                <p className="text-base font-semibold text-gray-900">Monthly Total</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCents(pricing.totalCents)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credits Summary */}
      {/* B8 Platform adoption: this read-only "Credits Allocation" information panel now
          uses the Platform Experience Card (@roi-pros/px-react) as its surface/container —
          the Platform owns the panel chrome (surface/border/radius/padding via tokens).
          The application retains its heading and all derived values (rendered as children).
          The app keeps its own <h2> rather than Card's `title` prop: Card renders a fixed
          <h3>, and forcing this one settings section to h3 would break the page's heading
          hierarchy (see Platform gap in the B8 report). Presentation only; no call sites. */}
      {pricing && (
        <PxCard api={{}}>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Credits Allocation</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">From Plan</p>
              <p className="text-xl font-semibold text-gray-900">{pricing.credits.fromPlan}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">From Outcomes</p>
              <p className="text-xl font-semibold text-gray-900">{pricing.credits.fromOutcomes}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 col-span-2">
              <p className="text-sm text-blue-600">Monthly Included</p>
              <p className="text-2xl font-bold text-blue-700">{pricing.credits.monthlyIncluded}</p>
            </div>
          </div>
          {pricing.credits.purchasedRemaining > 0 && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600">
                You have <span className="font-semibold">{pricing.credits.purchasedRemaining}</span> purchased credits remaining
              </p>
            </div>
          )}
        </PxCard>
      )}

      {/* Seat Details */}
      {pricing && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Team Seats</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-gray-900">
                {pricing.seats.current}
                <span className="text-lg font-normal text-gray-500">
                  {' '}/ {pricing.seats.included} included
                </span>
              </p>
              {pricing.seats.additional > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {pricing.seats.additional} additional seats at {formatCents(pricing.seats.perSeatCents)}/seat
                </p>
              )}
            </div>
            {pricing.plan.maxSeats && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Max seats</p>
                <p className="text-lg font-medium text-gray-900">{pricing.plan.maxSeats}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Billing Portal */}
      {!hasStripeCustomer && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <svg
            className="w-12 h-12 text-gray-400 mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
          <p className="text-gray-600 font-medium">No payment method on file</p>
          <p className="text-sm text-gray-500 mt-1">
            Add a payment method to upgrade your plan or purchase credits
          </p>
        </div>
      )}
    </div>
  );
}
